import { type NetworkType, useIntl } from '@capsicum/common';
import { Button, ButtonSection, InfoTooltip, ListCard, useRouter } from '@capsicum/common-web';
import {
	useWalletTransferOutCost,
	useWalletWithdrawRecipient,
	useWithdrawDataAtom
} from '@capsicum/features/wallet/shared';
import { useParams } from 'next/navigation';
import styled from 'styled-components';

import { WithdrawTransactionCard, type WithdrawTransactionCardProps } from '../../ui';

export const Confirmation = () => {
	const { push } = useRouter();
	const params = useParams();

	const recipientId = (params?.recipientId || '') as string;
	const { currencyId, fanToken, inputAmount: amount, selectedNetworkType: networkType } = useWithdrawDataAtom();

	const { data: transferOutCost, isLoading: isLoadingTransferOutCost } = useWalletTransferOutCost({
		amount,
		currencyId,
		withdrawToNetwork: networkType as NetworkType
	});

	const { data: recipient, isLoading: isRecipientLoading } = useWalletWithdrawRecipient(recipientId);

	const cardData: WithdrawTransactionCardProps = {
		isExternalToken: fanToken?.isExternalToken,
		partner: transferOutCost?.partner,
		recipient,
		withdrawInfo: {
			amount,
			chzAmount: transferOutCost?.formattedAmountToWithdraw,
			convertedPrice: transferOutCost?.formattedConvertedPrice,
			formattedAmount: transferOutCost?.formattedTotalAmount,
			isCHZWithdraw: transferOutCost?.isCHZWithdraw
		}
	};

	const { $t } = useIntl();

	const texts = {
		amountLabel: $t('wallet.withdraw.confirmation.amountLabel'),
		continueButtonLabel: $t('wallet.withdraw.confirmation.continueButtonLabel'),
		feeLabel: $t('wallet.withdraw.confirmation.feeLabel'),
		title: $t('wallet.withdraw.confirmation.pageTitle'),
		tooltip: $t('wallet.withdraw.confirmation.tooltip')
	};

	const isLoading = isRecipientLoading || isLoadingTransferOutCost;

	return (
		<S.Wrapper>
			<S.Container>
				<S.Title>{texts.title}</S.Title>
				{!isLoading && transferOutCost?.isCHZWithdraw && (
					<S.FeeContainer testID="withdraw-confirmation-feeCard">
						<S.FeeContent>
							<S.FeeRowContainer>
								<S.FeeTransactionLabel>{texts.amountLabel}</S.FeeTransactionLabel>
								<S.FeeTransactionText>{transferOutCost.formattedTotalAmount}</S.FeeTransactionText>
							</S.FeeRowContainer>
							<S.FeeRowContainer>
								<S.FeeTransactionTooltipLabel>
									{texts.feeLabel}
									<InfoTooltip contentPosition="bottom_right">{texts.tooltip}</InfoTooltip>
								</S.FeeTransactionTooltipLabel>
								<S.FeeTransactionLabel>{transferOutCost.formattedFeeAmount}</S.FeeTransactionLabel>
							</S.FeeRowContainer>
						</S.FeeContent>
					</S.FeeContainer>
				)}
				<WithdrawTransactionCard
					isExternalToken={cardData.isExternalToken}
					isLoading={isLoading}
					partner={cardData.partner}
					recipient={cardData.recipient}
					withdrawInfo={cardData.withdrawInfo}
				/>
				<ButtonSection>
					<Button
						disabled={isLoading}
						isLoading={isLoading}
						testID="withdraw-confirmation-submitButton"
						text={texts.continueButtonLabel}
						onClick={() => {
							push(`/wallet/withdraw/confirmation/${recipientId}/password`);
						}}
					/>
				</ButtonSection>
			</S.Container>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.xxlarge};
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) =>
				`${theme.spacings.large} ${theme.spacings.small} ${theme.spacings.small} ${theme.spacings.small}`};
		}
	`;
	export const Title = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin: 0 0 ${({ theme }) => theme.spacings.small};
	`;
	export const FeeContainer = styled(ListCard)`
		height: auto;
		margin: 0 0 ${({ theme }) => theme.spacings.medium} 0;
	`;

	export const FeeContent = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xsmall};
		display: flex;
		flex-direction: column;
		align-items: space-between;
	`;
	export const FeeTransactionLabel = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const FeeTransactionTooltipLabel = styled.span`
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: center;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const FeeTransactionText = styled.span`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const FeeRowContainer = styled.div`
		display: flex;
		justify-content: space-between;
	`;
}
